import { useState, type FormEvent } from "react";
import {
  useBillingPlans,
  useCreateBillingPlan,
  useUpdateBillingPlan,
  useToggleBillingPlanActive,
  useApplyBillingPlan,
  type ApplyBillingPlanResult,
  type BillingPlan,
} from "../api/hooks";
import { DataTable, createColumnHelper } from "../components/DataTable";
import {
  Button,
  Chip,
  ErrorNotice,
  Field,
  Input,
  Modal,
  Notice,
  PageHeader,
  Select,
} from "../components/ui";
import { IconPlus } from "../components/icons";

/**
 * Gestión de planes de facturación (CLAUDE.md Fase 7, paso 9).
 *
 * Solo super-admin llega a esta pantalla (el menú, la ruta y el servidor lo
 * comprueban). Un plan se puede desactivar (`isActive = false`) para que deje
 * de aparecer al dar de alta nuevos tenants, pero las suscripciones existentes
 * que lo referencian no se ven afectadas: la relación `Subscription →
 * BillingPlan` sigue siendo válida indefinidamente.
 */

const CURRENCIES = ["EUR", "USD", "GBP"];

const EMPTY_FORM = {
  name: "",
  basePrice: "",
  pricePerDriver: "",
  pricePerCargador: "",
  pricePerDeca: "",
  maxDecasPerMonth: "",
  currency: "EUR",
  isActive: true,
};

const column = createColumnHelper<BillingPlan>();

function fmtPrice(value: number): string {
  return value.toFixed(2);
}

export function BillingPlansPage() {
  const query = useBillingPlans();
  const create = useCreateBillingPlan();
  const update = useUpdateBillingPlan();
  const toggle = useToggleBillingPlanActive();
  const apply = useApplyBillingPlan();

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createValues, setCreateValues] = useState({ ...EMPTY_FORM });

  // Edit modal
  const [editPlan, setEditPlan] = useState<BillingPlan | null>(null);
  const [editValues, setEditValues] = useState({ ...EMPTY_FORM });

  // Aplicar a suscripciones: confirmación y resumen del resultado
  const [applyPlan, setApplyPlan] = useState<BillingPlan | null>(null);
  const [applyResult, setApplyResult] = useState<ApplyBillingPlanResult | null>(null);

  const openApply = (plan: BillingPlan): void => {
    apply.reset();
    setApplyResult(null);
    setApplyPlan(plan);
  };

  const closeApply = (): void => {
    setApplyPlan(null);
    setApplyResult(null);
  };

  const onApplyConfirm = (): void => {
    if (!applyPlan) return;
    apply.mutate(applyPlan.id, { onSuccess: (result) => setApplyResult(result) });
  };

  const openCreate = (): void => {
    setCreateValues({ ...EMPTY_FORM });
    setCreateOpen(true);
  };

  const openEdit = (plan: BillingPlan): void => {
    setEditPlan(plan);
    setEditValues({
      name: plan.name,
      basePrice: String(plan.basePrice),
      pricePerDriver: String(plan.pricePerDriver),
      pricePerCargador: String(plan.pricePerCargador),
      pricePerDeca: String(plan.pricePerDeca),
        maxDecasPerMonth: plan.maxDecasPerMonth !== null ? String(plan.maxDecasPerMonth) : "",
      currency: plan.currency,
      isActive: plan.isActive,
    });
  };

  const onCreateSubmit = (event: FormEvent): void => {
    event.preventDefault();
    create.mutate(
      {
        name: createValues.name.trim(),
        basePrice: parseFloat(createValues.basePrice) || 0,
        pricePerDriver: parseFloat(createValues.pricePerDriver) || 0,
        pricePerCargador: parseFloat(createValues.pricePerCargador) || 0,
        pricePerDeca: parseFloat(createValues.pricePerDeca) || 0,
        maxDecasPerMonth: createValues.maxDecasPerMonth ? parseInt(createValues.maxDecasPerMonth, 10) : null,
        currency: createValues.currency,
        isActive: createValues.isActive,
      },
      { onSuccess: () => setCreateOpen(false) },
    );
  };

  const onEditSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (!editPlan) return;
    update.mutate(
      {
        id: editPlan.id,
        values: {
          name: editValues.name.trim(),
          basePrice: parseFloat(editValues.basePrice) || 0,
          pricePerDriver: parseFloat(editValues.pricePerDriver) || 0,
          pricePerCargador: parseFloat(editValues.pricePerCargador) || 0,
          pricePerDeca: parseFloat(editValues.pricePerDeca) || 0,
          maxDecasPerMonth: editValues.maxDecasPerMonth ? parseInt(editValues.maxDecasPerMonth, 10) : null,
          currency: editValues.currency,
          isActive: editValues.isActive,
        },
      },
      { onSuccess: () => setEditPlan(null) },
    );
  };

  const columns = [
    column.accessor("name", { header: "Nombre" }),
    column.accessor("basePrice", {
      header: "Cuota base",
      cell: (info) => (
        <span className="num">
          {fmtPrice(Number(info.getValue()))} {info.row.original.currency}
        </span>
      ),
    }),
    column.accessor("pricePerDriver", {
      header: "Por conductor",
      cell: (info) => (
        <span className="num">
          {fmtPrice(Number(info.getValue()))} {info.row.original.currency}
        </span>
      ),
    }),
    column.accessor("pricePerCargador", {
      header: "Por cargador",
      cell: (info) => (
        <span className="num">
          {fmtPrice(Number(info.getValue()))} {info.row.original.currency}
        </span>
      ),
    }),
    column.accessor("pricePerDeca", {
      header: "Por DeCA",
      cell: (info) => (
        <span className="num">
          {fmtPrice(Number(info.getValue()))} {info.row.original.currency}
        </span>
      ),
    }),
    column.accessor("subscriberCount", {
      header: "Suscriptores",
      cell: (info) => (
        <span className="num">{info.getValue() ?? 0}</span>
      ),
    }),
    column.accessor("isActive", {
      header: "Estado",
      cell: (info) => {
        const plan = info.row.original;
        const isActive = info.getValue();
        return (
          <div className="flex items-center gap-2">
            {isActive ? <Chip tone="ok">Activo</Chip> : <Chip tone="neutral">Inactivo</Chip>}
            <Button
              variant="quiet"
              onClick={() => toggle.mutate({ id: plan.id, isActive: !isActive })}
              disabled={toggle.isPending}
              title={isActive ? "Desactivar plan" : "Activar plan"}
            >
              {isActive ? "Desactivar" : "Activar"}
            </Button>
          </div>
        );
      },
    }),
    column.display({
      id: "edit",
      header: "",
      cell: (info) => (
        <div className="flex items-center gap-2">
          <Button onClick={() => openEdit(info.row.original)}>Editar</Button>
          <Button
            variant="quiet"
            onClick={() => openApply(info.row.original)}
            title="Llevar a Stripe los valores actuales de este plan para todas las suscripciones que lo usan"
          >
            Aplicar a suscripciones
          </Button>
        </div>
      ),
    }),
  ];

  return (
    <>
      <PageHeader
        eyebrow="Facturación"
        title="Planes de facturación"
        description="Tarifas que se asignan a cada tenant al darlo de alta. Los precios son SIEMPRE sin impuestos: el impuesto (IVA, IGIC…) se suma encima según el tipo fiscal asignado a cada tenant (ver «Tipos fiscales»). Un plan desactivado ya no se ofrece en el formulario de alta, pero las suscripciones existentes que lo usan siguen siendo válidas."
        actions={
          <Button variant="primary" onClick={openCreate}>
            <IconPlus className="h-4 w-4" />
            Nuevo plan
          </Button>
        }
      />

      {create.isError ? (
        <div className="mb-4">
          <ErrorNotice error={create.error} />
        </div>
      ) : null}

      {query.isError ? (
        <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          data={query.data ?? []}
          isLoading={query.isLoading}
          emptyMessage="Todavía no hay planes de facturación."
        />
      )}

      {/* Modal crear */}
      <Modal open={createOpen} title="Nuevo plan de facturación" onClose={() => setCreateOpen(false)}>
        <form onSubmit={onCreateSubmit} className="space-y-3">
          <PlanFields values={createValues} onChange={setCreateValues} />
          {create.isError ? <ErrorNotice error={create.error} /> : null}
          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={create.isPending}>
              {create.isPending ? "Creando…" : "Crear plan"}
            </Button>
            <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          </div>
        </form>
      </Modal>

      {/* Modal aplicar a suscripciones */}
      <Modal
        open={applyPlan !== null}
        title={applyPlan ? `Aplicar «${applyPlan.name}» a sus suscripciones` : "Aplicar plan"}
        onClose={closeApply}
      >
        {applyPlan && !applyResult ? (
          <div className="space-y-3">
            <p className="text-sm">
              Se llevarán a Stripe los valores actuales de este plan para las{" "}
              <strong>{applyPlan.subscriberCount ?? 0}</strong> suscripciones que lo usan (también si el plan no ha
              cambiado de nombre). Editar un plan solo lo guarda aquí: hasta que lo apliques, las suscripciones ya
              creadas siguen cobrando con los precios antiguos.
            </p>
            <Notice tone="neutral">
              Solo se sustituyen los componentes cuyo precio ha cambiado, con prorrata: las líneas proporcionales
              se suman a la próxima factura y no se cobra nada en el momento. Si solo cambió el límite de DeCAs,
              no se toca ningún precio y no se pierde el uso del periodo.
            </Notice>
            <Notice tone="warn">
              Si ha cambiado el precio por DeCA, se descarta el uso de DeCAs ya registrado en el periodo en curso
              (se empieza a contar desde cero con el precio nuevo).
            </Notice>
            {apply.isError ? <ErrorNotice error={apply.error} /> : null}
            <div className="flex gap-2">
              <Button variant="primary" onClick={onApplyConfirm} disabled={apply.isPending}>
                {apply.isPending ? "Aplicando…" : "Aplicar ahora"}
              </Button>
              <Button onClick={closeApply} disabled={apply.isPending}>
                Cancelar
              </Button>
            </div>
          </div>
        ) : null}
        {applyResult ? (
          <div className="space-y-3">
            <Notice tone={applyResult.failed > 0 ? "warn" : "ok"}>
              {applyResult.total === 0
                ? "Ninguna suscripción con Stripe usa este plan: no había nada que aplicar."
                : `${applyResult.applied} actualizadas, ${applyResult.unchanged} ya estaban al día, ${applyResult.failed} con error.`}
            </Notice>
            {applyResult.results.length > 0 ? (
              <ul className="space-y-1 text-sm">
                {applyResult.results.map((r) => (
                  <li key={r.tenantId}>
                    <strong>{r.tenantName ?? r.tenantId}</strong>:{" "}
                    {r.outcome === "error"
                      ? `error — ${r.error ?? "desconocido"}`
                      : r.outcome === "unchanged"
                        ? "ya estaba al día"
                        : [
                            r.changedComponents.length > 0
                              ? `precios cambiados (${r.changedComponents.join(", ")})`
                              : "solo se actualizó el límite",
                            r.usageCleared ? "se descartó el uso de DeCAs del periodo" : null,
                          ]
                            .filter(Boolean)
                            .join("; ")}
                  </li>
                ))}
              </ul>
            ) : null}
            <Button onClick={closeApply}>Cerrar</Button>
          </div>
        ) : null}
      </Modal>

      {/* Modal editar */}
      <Modal
        open={editPlan !== null}
        title="Editar plan de facturación"
        onClose={() => setEditPlan(null)}
      >
        <form onSubmit={onEditSubmit} className="space-y-3">
          <PlanFields values={editValues} onChange={setEditValues} />
          {update.isError ? <ErrorNotice error={update.error} /> : null}
          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={update.isPending}>
              {update.isPending ? "Guardando…" : "Guardar cambios"}
            </Button>
            <Button onClick={() => setEditPlan(null)}>Cancelar</Button>
          </div>
        </form>
      </Modal>
    </>
  );
}

interface PlanFormValues {
  name: string;
  basePrice: string;
  pricePerDriver: string;
  pricePerCargador: string;
  pricePerDeca: string;
  maxDecasPerMonth: string;
  currency: string;
  isActive: boolean;
}

function PlanFields({
  values,
  onChange,
}: {
  values: PlanFormValues;
  onChange: (values: PlanFormValues) => void;
}) {
  const set = (partial: Partial<PlanFormValues>) => onChange({ ...values, ...partial });
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Nombre del plan" htmlFor="bp-name">
          <Input
            id="bp-name"
            required
            value={values.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="Plan Básico"
          />
        </Field>
        <Field label="Cuota base mensual" htmlFor="bp-base">
          <Input
            id="bp-base"
            type="number"
            min="0"
            step="0.01"
            required
            value={values.basePrice}
            onChange={(e) => set({ basePrice: e.target.value })}
            placeholder="0.00"
          />
        </Field>
        <Field label="Precio por conductor" htmlFor="bp-driver">
          <Input
            id="bp-driver"
            type="number"
            min="0"
            step="0.01"
            required
            value={values.pricePerDriver}
            onChange={(e) => set({ pricePerDriver: e.target.value })}
            placeholder="0.00"
          />
        </Field>
        <Field label="Precio por cargador" htmlFor="bp-cargador">
          <Input
            id="bp-cargador"
            type="number"
            min="0"
            step="0.01"
            required
            value={values.pricePerCargador}
            onChange={(e) => set({ pricePerCargador: e.target.value })}
            placeholder="0.00"
          />
        </Field>
        <Field label="Precio por DeCA emitido" htmlFor="bp-deca">
          <Input
            id="bp-deca"
            type="number"
            min="0"
            step="0.001"
            required
            value={values.pricePerDeca}
            onChange={(e) => set({ pricePerDeca: e.target.value })}
            placeholder="0.00"
          />
        </Field>
        <Field label="Máx. DeCA por periodo" htmlFor="bp-max-decas">
          <Input
            id="bp-max-decas"
            type="number"
            min="1"
            step="1"
            value={values.maxDecasPerMonth}
            onChange={(e) => set({ maxDecasPerMonth: e.target.value })}
            placeholder="Ilimitado"
          />
        </Field>
        <Field label="Moneda" htmlFor="bp-currency">
          <Select
            id="bp-currency"
            value={values.currency}
            onChange={(e) => set({ currency: e.target.value })}
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm text-ink-700 dark:text-ink-200">
        <input
          type="checkbox"
          checked={values.isActive}
          onChange={(e) => set({ isActive: e.target.checked })}
        />
        Plan activo (disponible para nuevas altas)
      </label>
    </>
  );
}
